import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

export default function DeliveryReport({ api, isAdmin, usernames, defaultUsername }) {
  const [records,      setRecords]      = useState([]);
  const [total,        setTotal]        = useState(0);
  const [pages,        setPages]        = useState(1);
  const [page,         setPage]         = useState(1);
  const [stats,        setStats]        = useState(null);
  const [summary,      setSummary]      = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [filterUser,   setFilterUser]   = useState(defaultUsername || '');
  const [filterJob,    setFilterJob]    = useState('');
  const [filterDelivered, setFilterDelivered] = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [search,       setSearch]       = useState('');
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [bulkAction,   setBulkAction]   = useState('');
  const [remarks,      setRemarks]      = useState('');
  const [updating,     setUpdating]     = useState(false);
  const [jobOptions,   setJobOptions]   = useState([]);
  const [initJob,      setInitJob]      = useState('');
  const [initUser,     setInitUser]     = useState(defaultUsername || '');
  const [initing,      setIniting]      = useState(false);
  const [viewMode,     setViewMode]     = useState('list'); // 'list' | 'summary'

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filterUser)      params.username  = filterUser;
      if (filterJob)       params.jobNo     = filterJob;
      if (filterDelivered !== '') params.delivered = filterDelivered;
      if (dateFrom)        params.dateFrom  = dateFrom;
      if (dateTo)          params.dateTo    = dateTo;
      if (search)          params.search    = search;
      const { data } = await api.get('/delivery', { params });
      setRecords(data.records);
      setTotal(data.total);
      setPages(data.pages);
      setStats(data.stats);
    } catch { toast.error('Failed to load delivery records'); }
    finally { setLoading(false); }
  }, [api, page, filterUser, filterJob, filterDelivered, dateFrom, dateTo, search]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = {};
      if (filterUser) params.username = filterUser;
      if (filterJob)  params.jobNo    = filterJob;
      if (dateFrom)   params.dateFrom = dateFrom;
      if (dateTo)     params.dateTo   = dateTo;
      const { data } = await api.get('/delivery/report/summary', { params });
      setSummary(data);
    } catch {}
  }, [api, filterUser, filterJob, dateFrom, dateTo]);

  const fetchJobOptions = useCallback(async () => {
    try {
      const params = { limit: 10000, page: 1 };
      if (filterUser) params.username = filterUser;
      const { data } = await api.get('/profiles', { params });
      const jobs = [...new Set(data.profiles.map(p => p.jobNo).filter(Boolean))].sort((a, b) => a - b);
      setJobOptions(jobs);
    } catch {}
  }, [api, filterUser]);

  useEffect(() => { setPage(1); }, [filterUser, filterJob, filterDelivered, dateFrom, dateTo, search]);
  useEffect(() => { fetchRecords(); fetchSummary(); }, [fetchRecords, fetchSummary]);
  useEffect(() => { fetchJobOptions(); }, [fetchJobOptions]);

  const handlePatch = async (id, delivered) => {
    try {
      await api.patch(`/delivery/${id}`, { delivered, deliveredBy: '' });
      toast.success(delivered ? '✅ Marked delivered' : '↩️ Marked pending');
      fetchRecords(); fetchSummary();
    } catch { toast.error('Update failed'); }
  };

  const handleBulkUpdate = async () => {
    if (!selectedIds.size || !bulkAction) { toast.error('Select records and an action'); return; }
    setUpdating(true);
    try {
      await api.patch('/delivery/bulk/update', {
        ids:       [...selectedIds],
        delivered: bulkAction === 'delivered',
        remarks,
      });
      toast.success(`${selectedIds.size} records updated`);
      setSelectedIds(new Set()); setBulkAction(''); setRemarks('');
      fetchRecords(); fetchSummary();
    } catch { toast.error('Bulk update failed'); }
    finally { setUpdating(false); }
  };

  const handleInit = async () => {
    if (!initUser || !initJob) { toast.error('Select user and job'); return; }
    setIniting(true);
    try {
      const { data } = await api.post('/delivery/init', { username: initUser, jobNo: parseInt(initJob) });
      toast.success(data.message);
      fetchRecords(); fetchSummary();
    } catch(e) { toast.error(e.response?.data?.message || 'Init failed'); }
    finally { setIniting(false); }
  };

  const toggleSelect = id => setSelectedIds(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => {
    const all = records.every(r => selectedIds.has(r._id));
    setSelectedIds(all ? new Set() : new Set(records.map(r => r._id)));
  };

  const pct = (d, t) => t > 0 ? Math.round(d / t * 100) : 0;

  return (
    <div>
      {/* ── Stats bar ────────────────────────────────────────────────────────── */}
      {stats && (
        <div className="stats-row" style={{ marginBottom: 16 }}>
          <div className="stat-card">
            <div className="stat-card-num">{stats.total}</div>
            <div className="stat-card-label">Total</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num green">{stats.delivered}</div>
            <div className="stat-card-label">Delivered</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num orange">{stats.pending}</div>
            <div className="stat-card-label">Pending</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num">{pct(stats.delivered, stats.total)}%</div>
            <div className="stat-card-label">Delivery Rate</div>
          </div>
        </div>
      )}

      {/* ── Init delivery records (admin only) ───────────────────────────────── */}
      {isAdmin && (
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: '.85rem', marginBottom: 10 }}>📦 Initialise Delivery Tracking for a Job</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select className="search-input" style={{ maxWidth: 160 }} value={initUser} onChange={e => setInitUser(e.target.value)}>
              <option value="">Select user…</option>
              {(usernames || []).map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <select className="search-input" style={{ maxWidth: 120 }} value={initJob} onChange={e => setInitJob(e.target.value)}>
              <option value="">Select job…</option>
              {jobOptions.map(j => <option key={j} value={j}>Job {j}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={handleInit} disabled={initing || !initUser || !initJob}>
              {initing ? '⏳' : '🚀 Init Tracking'}
            </button>
            <span style={{ color: 'var(--text-muted)', fontSize: '.78rem' }}>Creates delivery records for all profiles in that job</span>
          </div>
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
        <input className="search-input" style={{ flex: 1, minWidth: 160 }} placeholder="🔍 Search name, PRN, class…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {isAdmin && (
          <select className="search-input" style={{ maxWidth: 140 }} value={filterUser} onChange={e => setFilterUser(e.target.value)}>
            <option value="">All Users</option>
            {(usernames || []).map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        )}
        <select className="search-input" style={{ maxWidth: 120 }} value={filterJob} onChange={e => setFilterJob(e.target.value)}>
          <option value="">All Jobs</option>
          {jobOptions.map(j => <option key={j} value={j}>Job {j}</option>)}
        </select>
        <select className="search-input" style={{ maxWidth: 150 }} value={filterDelivered} onChange={e => setFilterDelivered(e.target.value)}>
          <option value="">All Status</option>
          <option value="false">⏳ Pending</option>
          <option value="true">✅ Delivered</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>From:</span>
          <input type="date" className="search-input" style={{ maxWidth: 140 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>To:</span>
          <input type="date" className="search-input" style={{ maxWidth: 140 }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); setFilterDelivered(''); setFilterJob(''); setSearch(''); }}>✕ Clear</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('list')}>📋 List</button>
          <button className={`btn btn-sm ${viewMode === 'summary' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('summary')}>📊 Summary</button>
        </div>
      </div>

      {/* ── Bulk action bar ───────────────────────────────────────────────────── */}
      {isAdmin && selectedIds.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', marginBottom: 10, background: 'rgba(var(--primary-rgb),.1)', border: '1px solid rgba(var(--primary-rgb),.3)', borderRadius: 'var(--radius-sm)' }}>
          <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--primary)' }}>✅ {selectedIds.size} selected</span>
          <select className="search-input" style={{ maxWidth: 160, padding: '4px 8px' }} value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
            <option value="">Action…</option>
            <option value="delivered">✅ Mark Delivered</option>
            <option value="pending">⏳ Mark Pending</option>
          </select>
          <input className="search-input" style={{ maxWidth: 200, padding: '4px 8px' }} placeholder="Remarks (optional)"
            value={remarks} onChange={e => setRemarks(e.target.value)} />
          <button className="btn btn-primary btn-sm" onClick={handleBulkUpdate} disabled={updating || !bulkAction}>
            {updating ? '⏳' : 'Apply'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelectedIds(new Set())}>✕ Clear</button>
        </div>
      )}

      {/* ── List view ─────────────────────────────────────────────────────────── */}
      {viewMode === 'list' && (
        loading ? (
          <div className="loading"><div className="spinner" /><p style={{ marginTop: 12 }}>Loading…</p></div>
        ) : records.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📭</div>
            <h3>No delivery records</h3>
            <p>{isAdmin ? 'Use "Init Tracking" above to create records for a job.' : 'No delivery records found.'}</p>
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {isAdmin && (
                      <th style={{ width: 36 }}>
                        <input type="checkbox" style={{ accentColor: 'var(--primary)', cursor: 'pointer', width: 15, height: 15 }}
                          checked={records.length > 0 && records.every(r => selectedIds.has(r._id))}
                          onChange={toggleAll} />
                      </th>
                    )}
                    <th>Name</th>
                    <th>Class</th>
                    <th>PRN</th>
                    <th>Job</th>
                    <th>Status</th>
                    <th>Delivered At</th>
                    <th>Delivered By</th>
                    <th>Remarks</th>
                    {isAdmin && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r._id} style={{ background: selectedIds.has(r._id) ? 'rgba(var(--primary-rgb),.07)' : undefined }}>
                      {isAdmin && (
                        <td>
                          <input type="checkbox" checked={selectedIds.has(r._id)} onChange={() => toggleSelect(r._id)}
                            style={{ accentColor: 'var(--primary)', cursor: 'pointer', width: 15, height: 15 }} />
                        </td>
                      )}
                      <td className="td-bold">{r.name || '—'}</td>
                      <td>{r.class || '—'}</td>
                      <td className="td-mono">{r.prnCode || '—'}</td>
                      <td><span className="badge badge-blue" style={{ fontSize: '.7rem' }}>Job{r.jobNo || '—'}</span></td>
                      <td>
                        <span className={`badge ${r.delivered ? 'badge-green' : 'badge-orange'}`}>
                          {r.delivered ? '✅ Delivered' : '⏳ Pending'}
                        </span>
                      </td>
                      <td className="td-mono" style={{ fontSize: '.76rem' }}>
                        {r.deliveredAt ? new Date(r.deliveredAt).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ fontSize: '.78rem' }}>{r.deliveredBy || '—'}</td>
                      <td style={{ fontSize: '.76rem', color: 'var(--text-muted)', maxWidth: 120 }}>{r.remarks || '—'}</td>
                      {isAdmin && (
                        <td>
                          <button
                            className={`btn btn-xs ${r.delivered ? 'btn-outline' : 'btn-success'}`}
                            onClick={() => handlePatch(r._id, !r.delivered)}
                            style={{ minWidth: 80 }}>
                            {r.delivered ? '↩️ Undo' : '✅ Deliver'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pages > 1 && (
              <div className="pagination">
                <button className="page-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                <button className="page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</button>
                {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                  const n = page <= 4 ? i + 1 : page - 3 + i;
                  if (n < 1 || n > pages) return null;
                  return <button key={n} className={`page-btn${n === page ? ' current' : ''}`} onClick={() => setPage(n)}>{n}</button>;
                })}
                <button className="page-btn" onClick={() => setPage(p => p + 1)} disabled={page === pages}>›</button>
                <button className="page-btn" onClick={() => setPage(pages)} disabled={page === pages}>»</button>
                <span className="page-info">Page {page}/{pages} · {total} total</span>
              </div>
            )}
          </>
        )
      )}

      {/* ── Summary view ──────────────────────────────────────────────────────── */}
      {viewMode === 'summary' && summary && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* By Class */}
          <div className="card" style={{ margin: 0 }}>
            <div className="card-title">📚 By Class</div>
            {summary.byClass.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>No data</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Class</th><th>Total</th><th>Delivered</th><th>Pending</th><th>Rate</th></tr></thead>
                  <tbody>
                    {summary.byClass.map(r => (
                      <tr key={r._id}>
                        <td className="td-bold">{r._id || 'Unknown'}</td>
                        <td className="td-mono">{r.total}</td>
                        <td className="td-mono" style={{ color: 'var(--success)' }}>{r.delivered}</td>
                        <td className="td-mono" style={{ color: 'var(--warning)' }}>{r.total - r.delivered}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 99, height: 6, overflow: 'hidden', minWidth: 60 }}>
                              <div style={{ height: '100%', width: `${pct(r.delivered, r.total)}%`, background: 'var(--success)', borderRadius: 99, transition: 'width .3s' }} />
                            </div>
                            <span style={{ fontSize: '.76rem', fontFamily: 'monospace' }}>{pct(r.delivered, r.total)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* By Job */}
          <div className="card" style={{ margin: 0 }}>
            <div className="card-title">📋 By Job</div>
            {summary.byJob.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>No data</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Job</th><th>Total</th><th>Delivered</th><th>Pending</th><th>Rate</th></tr></thead>
                  <tbody>
                    {summary.byJob.map(r => (
                      <tr key={r._id}>
                        <td><span className="badge badge-blue" style={{ fontSize: '.72rem' }}>Job{r._id}</span></td>
                        <td className="td-mono">{r.total}</td>
                        <td className="td-mono" style={{ color: 'var(--success)' }}>{r.delivered}</td>
                        <td className="td-mono" style={{ color: 'var(--warning)' }}>{r.total - r.delivered}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 99, height: 6, overflow: 'hidden', minWidth: 60 }}>
                              <div style={{ height: '100%', width: `${pct(r.delivered, r.total)}%`, background: 'var(--primary)', borderRadius: 99, transition: 'width .3s' }} />
                            </div>
                            <span style={{ fontSize: '.76rem', fontFamily: 'monospace' }}>{pct(r.delivered, r.total)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
